import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { X } from "lucide-react";

export type ToastTone = "success" | "error" | "info";

type ToastItem = {
  id: number;
  tone: ToastTone;
  title: string;
  message: string;
};

type ToastApi = {
  show: (opts: { tone?: ToastTone; title: string; message: string }) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 1;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts: { tone?: ToastTone; title: string; message: string }) => {
    const id = nextId++;
    const item: ToastItem = {
      id,
      tone: opts.tone ?? "info",
      title: opts.title,
      message: opts.message
    };
    setItems((prev) => [...prev, item]);
    window.setTimeout(() => dismiss(id), 4500);
  }, [dismiss]);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4">
        {items.map((t) => {
          const toneClass =
            t.tone === "success"
              ? "border-success/30 bg-success-soft text-success"
              : t.tone === "error"
                ? "border-danger/30 bg-danger-soft text-danger"
                : "border-info/30 bg-info-soft text-info";
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-lg border px-4 py-3 shadow-(--shadow-panel) ${toneClass}`}
              role="status"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{t.title}</div>
                <div className="mt-0.5 text-sm opacity-90">{t.message}</div>
              </div>
              <button
                type="button"
                className="rounded p-0.5 opacity-70 hover:opacity-100"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

/** Imperative bridge for MobX stores outside React trees. */
let imperativeShow: ToastApi["show"] | null = null;

export const bindToastApi = (show: ToastApi["show"]) => {
  imperativeShow = show;
};

export const toast = {
  show(opts: { tone?: ToastTone; title: string; message: string }) {
    if (imperativeShow) imperativeShow(opts);
    else console.warn("toast not bound", opts);
  }
};
